import React, { useState } from 'react';
import {
  Box,
  Divider,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { CaretRight, Scales, TrendDown, TrendUp } from 'phosphor-react';

import { GlassCard, GlassPageBg } from '../../../../components/glass';
import {
  IDreLinha,
  useFinanceiroDre,
  useFinanceiroDreComposicao,
} from '../../../../hooks/useFinanceiro';
import { AnimatedBar } from '../components/AnimatedBar';
import { FinanceiroPageHeader } from '../components/FinanceiroPageHeader';
import { brl } from '../components/format';
import { InfoPopover } from '../components/InfoPopover';
import { KpiCard } from '../components/KpiCard';
import { useFinanceiroFilters } from '../components/useFinanceiroFilters';

const pct = (parte: number, todo: number) => (todo > 0 ? (parte / todo) * 100 : 0);
const fmtPct = (v: number) => `${v.toFixed(1).replace('.', ',')}%`;

interface LinhasProps {
  titulo: string;
  cor: string;
  linhas: IDreLinha[];
  baseTotal: number;
  onSelect: (l: IDreLinha) => void;
}

const Linhas = ({ titulo, cor, linhas, baseTotal, onSelect }: LinhasProps) => {
  const max = Math.max(1, ...linhas.map(l => l.total));
  return (
    <Box>
      <Text fontSize="xs" fontWeight={700} color={cor} mb={3} textTransform="uppercase">
        {titulo}
      </Text>
      <Flex direction="column" gap={2}>
        {linhas.map(l => (
          <Box
            key={`${l.tipo}-${l.categoria}`}
            role="button"
            onClick={() => onSelect(l)}
            cursor="pointer"
            borderRadius="lg"
            px={2}
            py={1.5}
            transition="background 0.15s"
            _hover={{ bg: 'rgba(15,23,42,0.04)' }}
          >
            <Flex justify="space-between" fontSize="sm" mb={1} align="center" gap={2}>
              <Flex align="center" gap={1} minW={0}>
                <CaretRight size={12} color="#94A3B8" />
                <Text color="#0F172A" noOfLines={1}>
                  {l.categoria}
                </Text>
              </Flex>
              <Flex align="center" gap={2} whiteSpace="nowrap">
                <Text color="#94A3B8" fontSize="xs" w="48px" textAlign="right">
                  {fmtPct(pct(l.total, baseTotal))}
                </Text>
                <Text color="#0F172A" fontWeight={600} w="110px" textAlign="right">
                  {brl(l.total)}
                </Text>
              </Flex>
            </Flex>
            <AnimatedBar pct={l.total > 0 ? Math.max(3, (l.total / max) * 100) : 0} cor={cor} />
          </Box>
        ))}
        {linhas.length === 0 && (
          <Text fontSize="sm" color="#94A3B8">
            Sem lançamentos.
          </Text>
        )}
      </Flex>
    </Box>
  );
};

export const FinanceiroDrePage = () => {
  const f = useFinanceiroFilters();
  const { data, isError } = useFinanceiroDre({ base: 'competencia', ...f.apiParams });

  const receitas = (data?.linhas ?? []).filter(l => l.tipo === 'RECEITA');
  const despesas = (data?.linhas ?? []).filter(l => l.tipo === 'DESPESA');
  const receitaTotal = data?.receita_total ?? 0;
  const despesaTotal = data?.despesa_total ?? 0;
  const resultado = data?.resultado ?? 0;

  // Modal de composição (drill sintético -> analítico).
  const [sel, setSel] = useState<IDreLinha | null>(null);
  const comp = useFinanceiroDreComposicao(
    {
      base: 'competencia',
      ...f.apiParams,
      tipo: sel?.tipo ?? '',
      categoria: sel?.categoria ?? '',
    },
    !!sel,
  );
  const corSel = sel?.tipo === 'RECEITA' ? '#16A34A' : '#DC2626';

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        <FinanceiroPageHeader
          eyebrow="Resultado"
          titulo="DRE / Resultado"
          subtitulo="Receita × despesa por categoria — clique numa linha para ver a composição"
          icon={<Scales size={24} weight="fill" />}
          cor="#7C3AED"
          info={
            <Text>
              Resultado por categoria (regime de competência). A coluna <b>%</b> mostra o peso de
              cada rubrica; clique numa linha para abrir a <b>composição</b> (quem compõe o valor,
              por pessoa).
            </Text>
          }
        />

        {isError && (
          <GlassCard intensity="medium" p={6} mb={5} tint="#DC2626">
            <Text color="#71717A">Não foi possível carregar a DRE.</Text>
          </GlassCard>
        )}

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
          <KpiCard
            label="Receita"
            valor={receitaTotal}
            color="#16A34A"
            icon={<TrendUp size={20} weight="duotone" />}
            index={0}
            info={
              <InfoPopover title="Receita">
                Soma de <i>total</i> dos lançamentos de <b>RECEITA</b> no período/franquia (regime
                de competência).
              </InfoPopover>
            }
          />
          <KpiCard
            label="Despesa"
            valor={despesaTotal}
            color="#DC2626"
            icon={<TrendDown size={20} weight="duotone" />}
            index={1}
            info={
              <InfoPopover title="Despesa">
                Soma de <i>total</i> dos lançamentos de <b>DESPESA</b> no período/franquia (regime
                de competência).
              </InfoPopover>
            }
          />
          <KpiCard
            label="Resultado"
            valor={resultado}
            color={resultado >= 0 ? '#16A34A' : '#DC2626'}
            icon={<Scales size={20} weight="duotone" />}
            index={2}
            info={
              <InfoPopover title="Resultado">
                <b>Receita − Despesa</b> do período. Margem:{' '}
                <b>{fmtPct(pct(resultado, receitaTotal))}</b> da receita.
              </InfoPopover>
            }
          />
        </SimpleGrid>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          <GlassCard intensity="strong" p={6}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
              <Linhas
                titulo="Receitas"
                cor="#16A34A"
                linhas={receitas}
                baseTotal={receitaTotal}
                onSelect={setSel}
              />
              <Linhas
                titulo="Despesas"
                cor="#DC2626"
                linhas={despesas}
                baseTotal={despesaTotal}
                onSelect={setSel}
              />
            </SimpleGrid>
          </GlassCard>
        </motion.div>
      </Box>

      {/* Modal de composição da linha (analítico) */}
      <Modal
        isOpen={!!sel}
        onClose={() => setSel(null)}
        size="lg"
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay backdropFilter="blur(4px)" bg="rgba(15,23,42,0.35)" />
        <ModalContent borderRadius="2xl">
          <ModalHeader>
            <Text fontSize="xs" fontWeight={700} color="#94A3B8" textTransform="uppercase">
              Composição · {sel?.tipo === 'RECEITA' ? 'Receita' : 'Despesa'}
            </Text>
            <Flex justify="space-between" align="baseline" gap={3} mt={1} pr={8}>
              <Text fontSize="lg" fontWeight={700} color="#0F172A" noOfLines={2}>
                {sel?.categoria}
              </Text>
              <Text fontSize="lg" fontWeight={700} color={corSel} whiteSpace="nowrap">
                {brl(comp.data?.total ?? sel?.total ?? 0)}
              </Text>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Divider mb={3} />
            {comp.isLoading && (
              <Flex justify="center" py={8}>
                <Spinner color={corSel} />
              </Flex>
            )}
            {!comp.isLoading && (comp.data?.itens.length ?? 0) === 0 && (
              <Text fontSize="sm" color="#94A3B8" py={4} textAlign="center">
                Sem detalhe por pessoa para esta rubrica.
              </Text>
            )}
            {!comp.isLoading && (comp.data?.itens.length ?? 0) > 0 && (
              <Flex direction="column" gap={1}>
                <Flex
                  justify="space-between"
                  fontSize="10px"
                  fontWeight={700}
                  color="#94A3B8"
                  textTransform="uppercase"
                  px={2}
                  pb={1}
                >
                  <Text>Pessoa</Text>
                  <Text>Valor · %</Text>
                </Flex>
                {(comp.data?.itens ?? []).map(it => {
                  const total = comp.data?.total ?? 0;
                  return (
                    <Flex
                      key={`${it.pessoa_nome}-${it.valor}`}
                      justify="space-between"
                      align="center"
                      gap={3}
                      px={2}
                      py={1.5}
                      borderRadius="md"
                      _hover={{ bg: 'rgba(15,23,42,0.03)' }}
                    >
                      <Flex align="center" gap={2} minW={0}>
                        <Text fontSize="sm" color="#0F172A" noOfLines={1}>
                          {it.pessoa_nome || '—'}
                        </Text>
                        {it.qtd > 1 && (
                          <Text fontSize="11px" color="#94A3B8" whiteSpace="nowrap">
                            ({it.qtd} lanç.)
                          </Text>
                        )}
                      </Flex>
                      <Flex align="center" gap={3} whiteSpace="nowrap">
                        <Text fontSize="11px" color="#94A3B8" w="44px" textAlign="right">
                          {fmtPct(pct(it.valor, total))}
                        </Text>
                        <Text
                          fontSize="sm"
                          fontWeight={600}
                          color="#0F172A"
                          w="110px"
                          textAlign="right"
                        >
                          {brl(it.valor)}
                        </Text>
                      </Flex>
                    </Flex>
                  );
                })}
              </Flex>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </GlassPageBg>
  );
};
